import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Scramble } from '../../brand/Scramble';
import { Empty, LivePill, StatusPill, Toast, usePoll } from '../../components/admin/bits';
import { DeleteDialog } from '../../components/admin/DeleteDialog';
import { api } from '../../lib/api';
import { fmtDay, fmtDuration, fmtRelative } from '../../lib/format';
import type { Summary } from '../../lib/types';

export const copyLink = async (slug?: string): Promise<string> => {
  const url = slug ? `${window.location.origin}/p/${slug}` : window.location.origin;
  await navigator.clipboard.writeText(url).catch(() => undefined);
  return url;
};

export const ProposalList = () => {
  const load = useCallback(() => api<{ proposals: Summary[] }>('/api/admin/proposals'), []);
  const { data, error, reload } = usePoll(load, 15_000);
  const [toast, setToast] = useState('');
  const [doomed, setDoomed] = useState<Summary | null>(null);
  const navigate = useNavigate();

  if (!data) return error ? <p className="error">{error}</p> : <div className="center"><div className="spinner" /></div>;
  const list = data.proposals;

  return (
    <>
      <div className="ahead">
        <div>
          <p className="kick">Propostas</p>
          <h1>Todas as propostas.</h1>
        </div>
        <Link className="btn" to="/admin/propostas/nova">
          <Scramble>Nova proposta</Scramble>
        </Link>
      </div>

      {list.length === 0 ? (
        <Empty>Nenhuma proposta ainda. Crie a primeira.</Empty>
      ) : (
        <div className="panel table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Proposta</th>
                <th>Status</th>
                <th>Validade</th>
                <th className="r">Acessos</th>
                <th className="r">Aberturas</th>
                <th className="r">Leitura</th>
                <th className="r">Rolagem</th>
                <th className="r">Capturas</th>
                <th>Último acesso</th>
                <th>
                  <span className="sr">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="row-link" onClick={() => navigate(`/admin/propostas/${p.id}`)}>
                  <td>
                    <Link to={`/admin/propostas/${p.id}`} className="t-title" onClick={(e) => e.stopPropagation()}>
                      {p.title}
                    </Link>
                    <span className="t-sub">
                      {p.clientName} · v{p.versionNumber}
                      {p.pending > 0 && <span className="count">{p.pending}</span>}
                    </span>
                  </td>
                  <td>
                    <div className="t-pills">
                      <StatusPill status={p.status} />
                      <LivePill n={p.live} />
                    </div>
                  </td>
                  <td className="num">{fmtDay(p.expiresAt)}</td>
                  <td className="r num">{p.logins}</td>
                  <td className="r num">{p.opens}</td>
                  <td className="r num">{fmtDuration(p.activeMs)}</td>
                  <td className="r num">{p.maxScroll}%</td>
                  <td className="r num" data-risk={p.captures > 0 || undefined}>
                    {p.captures}
                  </td>
                  <td className="dim">{fmtRelative(p.lastSeenAt)}</td>
                  <td className="r" onClick={(e) => e.stopPropagation()}>
                    <div className="t-act">
                      <button className="btn-link small" onClick={async () => setToast(`Link copiado: ${await copyLink(p.slug)}`)}>
                        Copiar link
                      </button>
                      <button className="icon-btn" aria-label={`Excluir ${p.title}`} onClick={() => setDoomed(p)}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                          <path d="M4 6h12M8 6V4h4v2M6 6l.8 10h6.4L14 6" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DeleteDialog
        proposal={doomed}
        onClose={() => setDoomed(null)}
        onDeleted={() => {
          setDoomed(null);
          setToast('Proposta excluída.');
          reload();
        }}
      />
      {toast && <Toast text={toast} onDone={() => setToast('')} />}
    </>
  );
};
